import { useCallback } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import {
  getIsOnSale,
  getIsPending,
  getIsAdopted,
  getIsOnResale,
} from '@wix/da-shared-react/pkg/utils/adoptables';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { pushAdoptsSellerModal as pushAdoptsSellerModalAction } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';

export interface SellActionCallbacks {
  onSellClick: () => void;
  onEditAdoptableSaleClick: () => void;
  onEditPcpSaleClick: () => void;
}

export interface SellActionState {
  isUnsellable: boolean;
  isSold: boolean;
  isOnResale: boolean;
  isOnSale: boolean;
  isPending: boolean;
  isPcp: boolean;
  callbacks: SellActionCallbacks;
}

/**
 * Hook that centralizes the common sell action logic between SellButton and useSaleActions.
 *
 * @param deviation - The deviation to analyze for sell actions
 * @returns Object containing all the sell states and callbacks
 */
export function useSellActionLogic(deviation: PapiDeviation): SellActionState {
  const pushAdoptsSellerModal = useAction(pushAdoptsSellerModalAction);

  // Common callbacks used by both SellButton and useSaleActions
  const onSellClick = useCallback(() => {
    pushAdoptsSellerModal([deviation]);
  }, [pushAdoptsSellerModal, deviation]);

  const onEditAdoptableSaleClick = useCallback(() => {
    pushAdoptsSellerModal([deviation], 'update');
  }, [pushAdoptsSellerModal, deviation]);

  const onEditPcpSaleClick = useCallback(() => {
    pushAdoptsSellerModal([deviation], 'sell-pcp');
  }, [pushAdoptsSellerModal, deviation]);

  // Common state calculations
  const deviationType = getDeviationType(deviation);
  const isUnsellable = [
    DeviationTypes.LITERATURE,
    DeviationTypes.JOURNAL,
    DeviationTypes.STATUS,
  ].includes(deviationType);

  const isSold = getIsAdopted(deviation);
  const isOnResale = getIsOnResale(deviation);
  const isOnSale = getIsOnSale(deviation);
  const isPending = getIsPending(deviation);
  const isPcp = !!(deviation.isPurchasable || deviation.pcp);

  return {
    isUnsellable,
    isSold,
    isOnResale,
    isOnSale,
    isPending,
    isPcp,
    callbacks: {
      onSellClick,
      onEditAdoptableSaleClick,
      onEditPcpSaleClick,
    },
  };
}
